import { CSSProperties } from 'react';
import { theme as AntDesignTheme } from 'antd';
import { MappingAlgorithm, OverrideToken } from 'antd/es/theme/interface';
import { AliasToken, GlobalToken } from 'antd/es/theme/internal';

export type ComponentsConfig = {
    [key in keyof OverrideToken]?: OverrideToken[key] & {
        algorithm?: boolean | MappingAlgorithm | MappingAlgorithm[];
    };
};

// --- Component Tokens

export const platformButtonPaddingInline = {
    large: '0.75rem',
    middle: '0.5rem',
    small: '0.25rem',
} as const;

export const platformButtonComponentTokens = {
    paddingInline: platformButtonPaddingInline.middle,
    paddingInlineLG: platformButtonPaddingInline.large,
    paddingInlineSM: platformButtonPaddingInline.small,
} satisfies NonNullable<ComponentsConfig['Button']>;

export const applyPlatformComponentTokens = (components: ComponentsConfig = {}): ComponentsConfig => ({
    ...components,
    Button: {
        ...(components.Button ?? {}),
        ...platformButtonComponentTokens,
    },
});

export const defaultComponentTokens = applyPlatformComponentTokens();

export const defaultLightComponentTokens: ComponentsConfig = {
    ...defaultComponentTokens,
    Tabs: { horizontalItemGutter: 16 },
    Collapse: { headerBg: '#ffffff' },
    DatePicker: { sizePopupArrow: 0 },
};

export const defaultDarkComponentTokens: ComponentsConfig = {
    ...defaultComponentTokens,
    Tabs: { horizontalItemGutter: 16 },
    Collapse: { headerBg: '#141414' },
};

// --- Text Color Tokens

interface RGBColor {
    blue: number;
    green: number;
    red: number;
}

type PlatformThemeMode = 'dark' | 'light';

const textOpacityByMode = {
    dark: {
        primary: 0.85,
        quaternary: 0.25,
        secondary: 0.65,
        tertiary: 0.45,
    },
    light: {
        primary: 0.88,
        quaternary: 0.25,
        secondary: 0.65,
        tertiary: 0.45,
    },
} as const;

export const platformLightTextTokens = {
    colorText: '#1f1f1f',
    colorTextBase: '#000000',
    colorTextQuaternary: '#bfbfbf',
    colorTextSecondary: '#595959',
    colorTextTertiary: '#8c8c8c',
} as const;

export const platformDarkTextTokens = {
    colorText: '#dcdcdc',
    colorTextBase: '#ffffff',
    colorTextQuaternary: '#4f4f4f',
    colorTextSecondary: '#adadad',
    colorTextTertiary: '#7e7e7e',
} as const;

const parseColor = (color: string): RGBColor | undefined => {
    const normalizedColor = color.trim().toLowerCase();
    const shortHexMatch = normalizedColor.match(/^#([0-9a-f]{3})$/i);

    if (shortHexMatch) {
        const [red, green, blue] = shortHexMatch[1].split('').map((channel) => parseInt(`${channel}${channel}`, 16));

        return { blue, green, red };
    }

    const hexMatch = normalizedColor.match(/^#([0-9a-f]{6})$/i);

    if (hexMatch) {
        return {
            red: parseInt(hexMatch[1].slice(0, 2), 16),
            green: parseInt(hexMatch[1].slice(2, 4), 16),
            blue: parseInt(hexMatch[1].slice(4, 6), 16),
        };
    }

    const rgbMatch = normalizedColor.match(
        /^rgba?\(\s*(\d+(?:\.\d+)?)\s*[, ]\s*(\d+(?:\.\d+)?)\s*[, ]\s*(\d+(?:\.\d+)?)(?:\s*[,/]\s*(?:\d+(?:\.\d+)?|\.\d+))?\s*\)$/,
    );

    if (!rgbMatch) {
        return undefined;
    }

    return {
        red: Number(rgbMatch[1]),
        green: Number(rgbMatch[2]),
        blue: Number(rgbMatch[3]),
    };
};

const toHexChannel = (channel: number) =>
    Math.min(255, Math.max(0, Math.round(channel)))
        .toString(16)
        .padStart(2, '0');

const compositeColor = (foreground: RGBColor, background: RGBColor, opacity: number) =>
    `#${toHexChannel(foreground.red * opacity + background.red * (1 - opacity))}${toHexChannel(
        foreground.green * opacity + background.green * (1 - opacity),
    )}${toHexChannel(foreground.blue * opacity + background.blue * (1 - opacity))}`;

const getRelativeLuminance = ({ blue, green, red }: RGBColor) => {
    const toLinearChannel = (channel: number) => {
        const normalizedChannel = channel / 255;

        return normalizedChannel <= 0.04045 ? normalizedChannel / 12.92 : ((normalizedChannel + 0.055) / 1.055) ** 2.4;
    };

    return 0.2126 * toLinearChannel(red) + 0.7152 * toLinearChannel(green) + 0.0722 * toLinearChannel(blue);
};

export const getColorContrastRatio = (foregroundColor: string, backgroundColor: string) => {
    const foreground = parseColor(foregroundColor);
    const background = parseColor(backgroundColor);

    if (!foreground || !background) {
        return 0;
    }

    const foregroundLuminance = getRelativeLuminance(foreground);
    const backgroundLuminance = getRelativeLuminance(background);
    const lighterLuminance = Math.max(foregroundLuminance, backgroundLuminance);
    const darkerLuminance = Math.min(foregroundLuminance, backgroundLuminance);

    return (lighterLuminance + 0.05) / (darkerLuminance + 0.05);
};

export const ensureOpaqueColorContrast = (
    color: string,
    backgroundColor: string,
    adjustmentColor: '#000000' | '#ffffff',
    minimumContrast = 4.5,
) => {
    const foreground = parseColor(color);
    const background = parseColor(backgroundColor);
    const adjustment = parseColor(adjustmentColor);

    if (!foreground || !background || !adjustment) {
        return undefined;
    }

    const normalizedColor = compositeColor(foreground, foreground, 1);

    if (getColorContrastRatio(normalizedColor, backgroundColor) >= minimumContrast) {
        return normalizedColor;
    }

    for (let adjustmentWeight = 0.05; adjustmentWeight <= 1; adjustmentWeight += 0.05) {
        const adjustedColor = compositeColor(adjustment, foreground, adjustmentWeight);

        if (getColorContrastRatio(adjustedColor, backgroundColor) >= minimumContrast) {
            return adjustedColor;
        }
    }

    return adjustmentColor;
};

const createPlatformThemeAlgorithm =
    (baseAlgorithm: MappingAlgorithm, mode: PlatformThemeMode): MappingAlgorithm =>
    (seedToken) => {
        const mappedToken = baseAlgorithm(seedToken);
        const requestedTokens = seedToken as typeof seedToken & Pick<Partial<AliasToken>, 'colorBgContainer'>;
        const fallbackTokens = mode === 'dark' ? platformDarkTextTokens : platformLightTextTokens;
        const foreground = parseColor(mappedToken.colorTextBase ?? fallbackTokens.colorTextBase);
        const background = parseColor(
            requestedTokens.colorBgContainer ??
                mappedToken.colorBgContainer ??
                (mode === 'dark' ? '#141414' : '#ffffff'),
        );

        if (!foreground || !background) {
            return {
                ...mappedToken,
                colorText: fallbackTokens.colorText,
                colorTextQuaternary: fallbackTokens.colorTextQuaternary,
                colorTextSecondary: fallbackTokens.colorTextSecondary,
                colorTextTertiary: fallbackTokens.colorTextTertiary,
            };
        }

        const opacity = textOpacityByMode[mode];

        return {
            ...mappedToken,
            colorText: compositeColor(foreground, background, opacity.primary),
            colorTextQuaternary: compositeColor(foreground, background, opacity.quaternary),
            colorTextSecondary: compositeColor(foreground, background, opacity.secondary),
            colorTextTertiary: compositeColor(foreground, background, opacity.tertiary),
        };
    };

export const platformLightAlgorithm = createPlatformThemeAlgorithm(AntDesignTheme.defaultAlgorithm, 'light');
export const platformDarkAlgorithm = createPlatformThemeAlgorithm(AntDesignTheme.darkAlgorithm, 'dark');

// --- Default Styling Tokens (For Light & Dark Themes)

// export const neutralScale = {
//     gray1: '#ffffff',
//     gray2: '#fafafa',
//     gray3: '#f5f5f5',
//     gray4: '#f0f0f0',
//     gray5: '#d9d9d9',
//     gray6: '#bfbfbf',
//     gray7: '#8c8c8c',
//     gray8: '#595959',
//     gray9: '#434343',
//     gray10: '#262626',
//     gray11: '#1f1f1f',
//     gray12: '#141414',
//     gray13: '#000000',
// };

// Aliases
export const imgLogoStyling = { width: '100%', height: '100%', objectFit: 'contain' } as CSSProperties;

export const transitionStyling = { transition: 'transform 250ms ease' } as CSSProperties;

export const fontFamily = '"Inter", sans-serif';

/** Keep app typography consistent when a registered theme only supplies partial tokens. */
export const withPlatformFont = <T extends Partial<AliasToken>>(tokens: T): T & { fontFamily: string } => ({
    ...tokens,
    fontFamily: tokens.fontFamily?.trim() || fontFamily,
});

export const defaultTokens: Partial<AliasToken | GlobalToken> = {
    fontFamily: fontFamily,
    borderRadius: 4,
    fontSize: 14,
    lineHeight: 1.5,
    fontSizeIcon: 16,
    // Box Shadows
    boxShadow: `0 3px 6px -4px rgba(0,0,0,0.12),
                  0 6px 16px 0 rgba(0,0,0,0.08),
                  0 9px 28px 8px rgba(0,0,0,0.05)`,
    boxShadowSecondary: `0 6px 16px 0 rgba(0,0,0,0.08),
                           0 9px 28px 8px rgba(0,0,0,0.05),
                           0 12px 48px 16px rgba(0,0,0,0.03)`,
    boxShadowTertiary: `0 9px 28px 8px rgba(0,0,0,0.05),
                          0 12px 48px 16px rgba(0,0,0,0.03),
                          0 18px 88px 24px rgba(0,0,0,0.02)`,
};

// --- Theme Tokens

// minimal example token sets; extend with the tokens your app needs
export const defaultLightTokens: Partial<AliasToken | GlobalToken> = {
    ...defaultTokens,
    colorPrimary: '#1890ff',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    colorInfo: '#1890ff',
    colorTextBase: platformLightTextTokens.colorTextBase,
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgBase: '#fafafa',
    colorBorder: '#f2f2f2',
    colorBorderSecondary: '#f2f2f2',
    colorSplit: '#f2f2f2',
    colorBgMask: 'rgb(0,0,0,0.05)',
};

export const defaultDarkTokens: Partial<AliasToken | GlobalToken> = {
    ...defaultTokens,
    colorPrimary: '#40a9ff',
    colorSuccess: '#3fbe3b',
    colorWarning: '#fadb14',
    colorError: '#ff7875',
    colorInfo: '#40a9ff',
    colorTextBase: platformDarkTextTokens.colorTextBase,
    colorBgContainer: '#141414',
    colorBgElevated: '#141414',
    colorBgBase: '#1f1f1f',
    colorBorder: '#262626',
    colorBorderSecondary: '#262626',
    // colorIcon: '#ffffff', // ?
    colorSplit: '#262626',
    colorBgMask: 'rgb(0,0,0,0.50)',
};

// export const graphiteTokens: Partial<AliasToken | GlobalToken> = {
//     ...defaultTokens,
//     colorPrimary: '#61afef', // blue
//     colorSuccess: '#98c379', // soft green
//     colorWarning: '#e5c07b', // muted yellow
//     colorError: '#e06c75', // muted red
//     colorInfo: '#56b6c2', // cyan
//     colorBgBase: '#282c34',
//     colorBgContainer: '#21252b',
//     colorBorder: '#3e4451',
// };

// export const bubblegumTokens: Partial<AliasToken | GlobalToken> = {
//     ...defaultTokens,
//     colorPrimary: '#ec4899', // vibrant rose
//     colorSuccess: '#f9a8d4',
//     colorWarning: '#fcd34d',
//     colorError: '#f43f5e',
//     colorInfo: '#f472b6',
//     colorTextBase: '#4a044e',
//     colorTextSecondary: '#6b21a8',
//     colorTextDisabled: '#9d174d',
//     colorBgBase: '#ffe4ef',
//     colorBgContainer: '#fff0f6',
//     colorBorder: '#fbcfe8',
//     colorBorderSecondary: '#f9a8d4',
// };

// export const retroTokens: Partial<AliasToken | GlobalToken> = {
//     ...defaultTokens,
//     colorPrimary: '#00ff7f', // neon green
//     colorSuccess: '#39ff14',
//     colorWarning: '#ffd700',
//     colorError: '#ff073a',
//     colorInfo: '#00bfff',
//     colorTextBase: '#d1fae5',
//     colorTextSecondary: '#6ee7b7',
//     colorTextDisabled: '#34d399',
//     colorBgBase: '#0a0a0a',
//     colorBgContainer: '#000000',
//     colorBorder: '#1f2937',
//     colorBorderSecondary: '#374151',
// };

// export const spreadsheetJockeyTokens: Partial<AliasToken | GlobalToken> = {
//     ...defaultTokens,
//     colorPrimary: '#217346', // Excel green
//     colorSuccess: '#2e8b57', // XP "Start" green
//     colorWarning: '#e6a23c', // soft amber
//     colorError: '#cc3333', // classic XP error red
//     colorInfo: '#1e90ff', // XP bright blue

//     colorTextBase: '#1f2937', // rich neutral gray
//     colorTextSecondary: '#4b5563', // softer gray
//     colorTextDisabled: '#9ca3af', // muted gray

//     colorBgBase: '#f0f6ff', // XP window base (soft blue-gray)
//     colorBgContainer: '#ffffff', // clean white containers
//     colorBorder: '#cbd5e1', // subtle cool gray borders
//     colorBorderSecondary: '#dbeafe', // light XP sky blue borders
// };
